import { useSyncExternalStore } from "react"

export type ResolvedAppearance = "light" | "dark"
export type Appearance = ResolvedAppearance | "system"

export type UseAppearanceReturn = {
  readonly appearance: Appearance
  readonly resolvedAppearance: ResolvedAppearance
  readonly updateAppearance: (mode: Appearance) => void
}

const storageKey = "resolto-theme"
const eventName = "resolto:theme-change"

function prefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
}

function getAppearance(): Appearance {
  if (typeof window === "undefined") return "system"
  const stored = localStorage.getItem(storageKey)
  return stored === "light" || stored === "dark" ? stored : "system"
}

function resolveAppearance(mode: Appearance): ResolvedAppearance {
  return mode === "system" ? (prefersDark() ? "dark" : "light") : mode
}

function applyAppearance(mode: Appearance): void {
  if (typeof document === "undefined") return
  const resolved = resolveAppearance(mode)
  document.documentElement.dataset.theme = resolved
  document.documentElement.classList.toggle("dark", resolved === "dark")
  document.documentElement.style.colorScheme = resolved
}

function subscribe(listener: () => void): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)")
  const updateForSystem = () => {
    if (getAppearance() === "system") {
      applyAppearance("system")
      listener()
    }
  }

  window.addEventListener(eventName, listener)
  media.addEventListener("change", updateForSystem)
  return () => {
    window.removeEventListener(eventName, listener)
    media.removeEventListener("change", updateForSystem)
  }
}

export function initializeTheme(): void {
  applyAppearance(getAppearance())
}

export function useAppearance(): UseAppearanceReturn {
  const appearance = useSyncExternalStore<Appearance>(subscribe, getAppearance, () => "system")
  const resolvedAppearance = resolveAppearance(appearance)

  const updateAppearance = (mode: Appearance): void => {
    if (mode === "system") localStorage.removeItem(storageKey)
    else localStorage.setItem(storageKey, mode)
    applyAppearance(mode)
    window.dispatchEvent(new Event(eventName))
  }

  return { appearance, resolvedAppearance, updateAppearance } as const
}
